import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowRight, ExternalLink } from 'lucide-react';

const DemoLoginPage = () => {
  const [email, setEmail] = useState('demo@demo.com');
  const [isLoading, setIsLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [demoLoginUrl, setDemoLoginUrl] = useState(null);
  
  const { requestMagicLink, setNeedsOnboarding, verifyMagicLink, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle magic link verification from URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyMagicLink(token);
    }
  }, [searchParams, verifyMagicLink]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    try {
      const response = await requestMagicLink(email);
      
      if (response.demo_login_url) {
        setLinkSent(true);
        setDemoLoginUrl(response.demo_login_url);
        toast.success('Login link sent!', { 
          description: 'Check your email (demo: use the button below)' 
        });
      } else {
        setNeedsOnboarding(true);
        toast.info('Welcome!', { description: 'Let\'s set up your account' });
        navigate('/onboarding', { state: { email } });
      }
    } catch (error) {
      console.error('Magic link request failed:', error);
      toast.error('Error', { 
        description: error.response?.data?.detail || 'Failed to send login link' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = () => {
    if (demoLoginUrl) {
      navigate(demoLoginUrl);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Active Demo Badge - Only shows on demo page */}
      <a
        href="https://www.birdenmedia.co"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-4 right-4 z-50 inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full text-sm font-semibold hover:bg-green-700 transition-colors"
        data-testid="demo-badge"
      >
        <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
        Active Demo
      </a>
      {/* Left Side - Demo Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo - ContractorOS branding */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black tracking-tight text-foreground mb-2">
              <span className="bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Contractor</span>
              <span className="text-foreground">OS</span>
            </h1>
            <p className="text-xs text-muted-foreground tracking-widest uppercase">by BirdenMedia</p>
            <p className="text-muted-foreground text-lg mt-4">
              Demo Mode
            </p>
          </div>
          
          {/* Demo Badge */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <p className="text-amber-800 text-sm text-center">
              <strong>Demo Mode:</strong> Explore the portal with sample data. 
              No account needed.
            </p>
          </div>
          
          {!linkSent ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="card-default">
                <h2 className="font-heading text-2xl font-semibold mb-6 text-center">
                  Try the Demo
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-lg mb-2 block">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="demo@demo.com"
                      className="input-default w-full"
                      required
                      data-testid="demo-email-input"
                    />
                    <p className="text-sm text-muted-foreground mt-2">
                      Use <button type="button" onClick={() => setEmail('demo@demo.com')} className="text-primary font-medium hover:underline">demo@demo.com</button> to explore
                    </p>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isLoading || !email}
                    className="btn-primary w-full"
                    data-testid="demo-submit-btn"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Mail className="w-5 h-5" />
                        Send Demo Link
                      </span>
                    )}
                  </Button>
                </div>
                
                <p className="text-center text-muted-foreground mt-6">
                  Magic link login - no password needed.
                </p>
              </div>
              
              <div className="text-center">
                <p className="text-muted-foreground">
                  Have an account?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-primary font-medium hover:underline"
                    data-testid="back-to-login-link"
                  >
                    Sign in here
                  </button>
                </p>
              </div>
            </form>
          ) : (
            <div className="card-default text-center animate-scale-in">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              
              <h2 className="font-heading text-2xl font-semibold mb-2">
                Demo Link Ready
              </h2>
              <p className="text-muted-foreground mb-6">
                Click below to access the demo portal
              </p>
              
              {demoLoginUrl && (
                <div className="space-y-4">
                  <Button
                    onClick={handleDemoLogin}
                    className="btn-secondary w-full"
                    data-testid="open-demo-btn"
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Enter Demo Portal
                  </Button>
                  
                  <p className="text-sm text-muted-foreground">
                    In production, this link would be sent via email.
                  </p>
                </div>
              )}
              
              <Button
                variant="ghost"
                onClick={() => {
                  setLinkSent(false);
                  setDemoLoginUrl(null);
                }}
                className="mt-4"
              >
                Use a different email
              </Button>
            </div>
          )}
          
          {/* Footer */}
          <p className="text-center text-sm text-muted-foreground mt-8">
            Powered by{' '}
            <a href="https://birdenmedia.co" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
              BirdenMedia
            </a>
          </p>
        </div>
      </div>
      
      {/* Right Side - Image (Desktop only) */}
      <div className="hidden lg:flex flex-1 bg-primary relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/6474207/pexels-photo-6474207.jpeg"
          alt="Contractor at work"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary/70" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <h2 className="font-heading text-4xl font-bold mb-4">
            Run Your Business<br />Like a Pro
          </h2>
          <p className="text-xl opacity-90 max-w-md">
            Create estimates, send invoices, and get paid faster. 
            All from your phone or tablet.
          </p>
          
          <div className="mt-12 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <ArrowRight className="w-4 h-4" />
              </div>
              <span className="text-lg">Simple estimate creation</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <ArrowRight className="w-4 h-4" />
              </div>
              <span className="text-lg">Client approval in one click</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <ArrowRight className="w-4 h-4" />
              </div>
              <span className="text-lg">Professional PDF invoices</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoLoginPage;
